import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, Switch, message, Popconfirm, Tooltip, Select, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';

const Aliases = () => {
    const [aliases, setAliases] = useState([]);
    const [domains, setDomains] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingAlias, setEditingAlias] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                if (!selectedCluster) {
                    const defaultCluster = list.find(c => c.is_default);
                    if (defaultCluster) {
                        setSelectedCluster(defaultCluster.id);
                    } else if (list.length > 0) {
                        setSelectedCluster(list[0].id);
                    }
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchDomains = async () => {
        if (!selectedCluster) return;
        try {
            const res = await adminApi.get('/webmail/domains', {
                params: { cluster_id: selectedCluster, per_page: 100 }
            });
            if (res.data.code === 0) {
                setDomains(res.data.data.list || []);
            }
        } catch (error) {
            message.error('获取域名列表失败');
        }
    };

    const fetchAliases = async (page = 1, pageSize = 20) => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const params = {
                cluster_id: selectedCluster,
                page,
                per_page: pageSize
            };
            if (selectedDomain) {
                params.domain_id = selectedDomain;
            }
            const res = await adminApi.get('/webmail/aliases', { params });
            if (res.data.code === 0) {
                const data = res.data.data;
                setAliases(data.aliases || data.list || []);
                setPagination({
                    current: data.page || page,
                    pageSize: data.per_page || pageSize,
                    total: data.total || 0,
                });
            }
        } catch (error) {
            message.error('获取别名列表失败');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchDomains();
            fetchAliases();
        }
    }, [selectedCluster]);

    useEffect(() => {
        if (selectedCluster) {
            fetchAliases();
        }
    }, [selectedDomain]);

    const handleAdd = () => {
        setEditingAlias(null);
        form.resetFields();
        form.setFieldsValue({
            active: true,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingAlias(record);
        form.setFieldsValue({
            ...record,
            source_local: record.source_address?.split('@')[0] || '',
            source_domain_id: record.domain_id,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await adminApi.delete(`/webmail/aliases/${id}`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                message.success('删除成功');
                fetchAliases(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const domain = domains.find(d => d.id === values.source_domain_id);
            const data = {
                cluster_id: selectedCluster,
                domain_id: values.source_domain_id,
                source: `${values.source_local}@${domain?.name || ''}`,
                destination: values.destination_address,
                active: values.active ? 1 : 0,
                comment: values.comment || '',
            };

            if (editingAlias) {
                const res = await adminApi.put(`/webmail/aliases/${editingAlias.id}`, data);
                if (res.data.code === 0) {
                    message.success('更新成功');
                    setModalVisible(false);
                    fetchAliases(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.message || '更新失败');
                }
            } else {
                const res = await adminApi.post('/webmail/aliases', data);
                if (res.data.code === 0) {
                    message.success('创建成功');
                    setModalVisible(false);
                    fetchAliases(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.message || '创建失败');
                }
            }
        } catch (error) {
            message.error('操作失败');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (record) => {
        try {
            const res = await adminApi.post(`/webmail/aliases/${record.id}/active`, {
                cluster_id: selectedCluster,
                active: !record.active,
            });
            if (res.data.code === 0) {
                message.success(record.active ? '已禁用' : '已启用');
                fetchAliases(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: '源地址',
            dataIndex: 'source_address',
            render: (addr) => <strong>{addr}</strong>,
        },
        {
            title: '',
            width: 50,
            render: () => <SwapOutlined style={{ color: '#1890ff' }} />,
        },
        {
            title: '目标地址',
            dataIndex: 'destination_address',
            render: (addr) => <Tag color="blue">{addr}</Tag>,
        },
        {
            title: '状态',
            dataIndex: 'active',
            width: 80,
            render: (active, record) => (
                <Switch
                    checked={active}
                    size="small"
                    onChange={() => handleToggleActive(record)}
                />
            ),
        },
        {
            title: '备注',
            dataIndex: 'comment',
            ellipsis: true,
            width: 150,
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 160,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '操作',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="编辑">
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    </Tooltip>
                    <Popconfirm
                        title="确定删除此别名吗？"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="邮箱别名管理"
            extra={
                <Space>
                    <Select
                        style={{ width: 150 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Select
                        style={{ width: 200 }}
                        value={selectedDomain}
                        onChange={setSelectedDomain}
                        placeholder="全部域名"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                    >
                        {domains.map(d => (
                            <Select.Option key={d.id} value={d.id}>
                                {d.name}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => fetchAliases(pagination.current, pagination.pageSize)} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedCluster}>
                        添加别名
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={aliases}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={selectedCluster ? '暂无别名，点击右上角“添加别名”创建' : '请先选择集群'} /> }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                    onChange: (page, pageSize) => fetchAliases(page, pageSize),
                }}
            />

            <Modal
                title={editingAlias ? '编辑别名' : '添加别名'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        label="源地址"
                        required
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                                name="source_local"
                                noStyle
                                rules={[{ required: true, message: '请输入本地部分' }]}
                            >
                                <Input style={{ width: '50%' }} placeholder="本地部分" />
                            </Form.Item>
                            <Input style={{ width: '10%', textAlign: 'center' }} disabled value="@" />
                            <Form.Item
                                name="source_domain_id"
                                noStyle
                                rules={[{ required: true, message: '请选择域名' }]}
                            >
                                <Select style={{ width: '40%' }} placeholder="选择域名">
                                    {domains.map(d => (
                                        <Select.Option key={d.id} value={d.id}>
                                            {d.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item
                        name="destination_address"
                        label="目标地址"
                        rules={[
                            { required: true, message: '请输入目标邮箱地址' },
                            { type: 'email', message: '请输入有效的邮箱地址' }
                        ]}
                    >
                        <Input placeholder="转发到此邮箱地址" />
                    </Form.Item>
                    <Form.Item name="comment" label="备注">
                        <Input.TextArea rows={2} placeholder="可选备注" />
                    </Form.Item>
                    <Form.Item name="active" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Aliases;
